/**
 * SubscriptionFeatureRegistry - Maps subscription features to their constituent privileges.
 *
 * A "feature" is a named bundle of related privileges that admins can enable/disable per tier.
 * This registry provides:
 * - Feature → Privileges mapping (for runtime expansion)
 * - Privilege → Feature reverse lookup (for error messages)
 * - Feature metadata for admin UI (category, description, default state)
 *
 * IMPORTANT: This registry does NOT mutate SecurityRegistry. Privileges must declare
 * `subscriptionGated: true` and `subscriptionFeatureId` at registration time.
 */

export type FeatureCategory = 'search' | 'collaboration' | 'content' | 'ai' | 'admin';

export interface FeatureDefinition {
  /** Unique feature identifier (e.g., 'search:semantic', 'comments') */
  id: string;
  /** Human-readable name for admin UI */
  name: string;
  /** Description shown in admin UI */
  description: string;
  /** Category for grouping in admin UI */
  category: FeatureCategory;
  /** Privilege IDs this feature grants when enabled */
  privileges: string[];
  /** Whether this feature is enabled by default on new tiers */
  defaultEnabled: boolean;
}

export class SubscriptionFeatureRegistry {
  private static instance: SubscriptionFeatureRegistry | null = null;
  private features = new Map<string, FeatureDefinition>();
  private privilegeToFeature = new Map<string, string>();
  private locked = false;

  private constructor() {}

  public static getInstance(): SubscriptionFeatureRegistry {
    if (!SubscriptionFeatureRegistry.instance) {
      SubscriptionFeatureRegistry.instance = new SubscriptionFeatureRegistry();
    }
    return SubscriptionFeatureRegistry.instance;
  }

  /**
   * Reset the singleton (for testing only)
   */
  public static resetInstance(): void {
    SubscriptionFeatureRegistry.instance = null;
  }

  /**
   * Register a subscription feature.
   * Must be called before lock() during bootstrap.
   */
  public registerFeature(def: FeatureDefinition): void {
    if (this.locked) {
      throw new Error(`SubscriptionFeatureRegistry is locked. Cannot register feature '${def.id}' after lock.`);
    }

    if (this.features.has(def.id)) {
      throw new Error(`Feature '${def.id}' is already registered.`);
    }

    this.features.set(def.id, def);

    // Build reverse lookup (privilege → feature)
    for (const privId of def.privileges) {
      if (this.privilegeToFeature.has(privId)) {
        throw new Error(
          `Privilege '${privId}' is already mapped to feature '${this.privilegeToFeature.get(privId)}'. ` +
            `Cannot map to '${def.id}'. Each privilege can only belong to one feature.`
        );
      }
      this.privilegeToFeature.set(privId, def.id);
    }
  }

  /**
   * Lock the registry to prevent further modifications.
   * Should be called after all features are registered during bootstrap.
   */
  public lock(): void {
    this.locked = true;
  }

  /**
   * Check if the registry is locked.
   */
  public isLocked(): boolean {
    return this.locked;
  }

  /**
   * Get a feature definition by ID.
   */
  public getFeature(featureId: string): FeatureDefinition | undefined {
    return this.features.get(featureId);
  }

  /**
   * Get the feature ID that a privilege belongs to.
   * Returns undefined if the privilege is not subscription-gated.
   */
  public getFeatureForPrivilege(privilegeId: string): string | undefined {
    return this.privilegeToFeature.get(privilegeId);
  }

  /**
   * Check if a privilege is mapped to a subscription feature.
   */
  public isPrivilegeMapped(privilegeId: string): boolean {
    return this.privilegeToFeature.has(privilegeId);
  }

  /**
   * Expand a list of feature/entitlement IDs to their constituent privilege IDs.
   * Unknown feature IDs are skipped (logged as warning).
   */
  public expandEntitlementsToPrivileges(entitlements: string[]): Set<string> {
    const result = new Set<string>();

    for (const entId of entitlements) {
      const feature = this.features.get(entId);
      if (feature) {
        for (const privId of feature.privileges) {
          result.add(privId);
        }
      }
      // If entitlement is not a known feature, it might be a raw privilege ID (escape hatch)
      // In that case, add it directly
      else if (!this.features.has(entId)) {
        result.add(entId);
      }
    }

    return result;
  }

  /**
   * Check if a specific privilege is granted by a set of entitlements.
   */
  public isPrivilegeGrantedByEntitlements(privilegeId: string, entitlements: string[]): boolean {
    const allowedPrivileges = this.expandEntitlementsToPrivileges(entitlements);
    return allowedPrivileges.has(privilegeId);
  }

  /**
   * Get all registered features.
   */
  public getAllFeatures(): FeatureDefinition[] {
    return Array.from(this.features.values());
  }

  /**
   * Get all features grouped by category.
   */
  public getFeaturesByCategory(): Map<FeatureCategory, FeatureDefinition[]> {
    const result = new Map<FeatureCategory, FeatureDefinition[]>();
    const allFeatures = Array.from(this.features.values());

    for (const feature of allFeatures) {
      const existing = result.get(feature.category) ?? [];
      existing.push(feature);
      result.set(feature.category, existing);
    }

    return result;
  }

  /**
   * Get feature IDs that are enabled by default.
   */
  public getDefaultEnabledFeatures(): string[] {
    return Array.from(this.features.values())
      .filter((f) => f.defaultEnabled)
      .map((f) => f.id);
  }

  /**
   * Validate that all privileges in a feature are registered in SecurityRegistry.
   * Returns array of invalid privilege IDs.
   */
  public validatePrivileges(getPrivilege: (id: string) => unknown): string[] {
    const invalid: string[] = [];
    const allFeatures = Array.from(this.features.values());

    for (const feature of allFeatures) {
      for (const privId of feature.privileges) {
        if (!getPrivilege(privId)) {
          invalid.push(privId);
        }
      }
    }

    return invalid;
  }
}
